import get from 'lodash.get';
import { useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useFragment, useRelayEnvironment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { connect } from 'react-redux';
import { executeCaptcha } from 'dibs-recaptcha/exports/recaptcha';
import { Link } from 'dibs-elements/exports/Link';

import {
    resetPayPalError,
    payPalVaulted,
    payPalError as payPalErrorAction,
    payPalRemoved,
    togglePayPalSaving,
    togglePayPalRemoving,
} from '../../actions/checkoutPaymentActions';
import CheckoutMutationWrapper from '../../mutations/CheckoutMutationWrapper';
import { shouldVerifyPaymentMethod } from '../../helpers/checkoutRedirects';
import { getGuestUserId } from '../../helpers/checkoutGuestUserToken';
import { guestCheckoutWalletUpdater } from '../helpers/guestCheckoutWalletUpdater';
import { logCheckoutError } from '../../helpers/logCheckoutError';
import { trackCompletedStep, trackUserInteractionOnce } from '../../helpers/checkoutTracking';
import { STEPS } from '../../constants/checkoutConstants';
import { CheckoutServerVarsContext } from '../../components/shared/CheckoutServerVarsContext';
import { useCheckoutIsLoggedIntoPayPal } from './useCheckoutIsLoggedIntoPayPal';
import { usePayPalPaymentMethod } from './usePayPalPaymentMethod';
import { usePayPalClient } from './usePayPalClient';

// components
import RowContainer from '../../shared/CheckoutRowContainer';
import CheckoutRemoveAction from '../../components/shared/actions/CheckoutRemoveAction';
import LocalSpinner from '../../shared/components/LocalSpinner';

import styles from './styles/checkoutPayPalWrapper.scss';

const CheckoutPayPalWrapper = ({
    viewer: viewerRef,
    buyer: buyerRef,
    shippingAddressId,
    wrapperClass = '',
    isPayPalSaving,
    isPayPalRemoving,
    onPayPalVaulted,
    onResetPayPalError,
    onPayPalRemoved,
    lastValidCreditCardToken,
    onTogglePayPalRemoving,
    onTogglePayPalSaving,
    onPayPalError,
    isPaymentLoading,
}) => {
    const viewer = useFragment(
        graphql`
            fragment CheckoutPayPalWrapper_viewer on Viewer {
                id
                ...usePayPalClient_viewer
            }
        `,
        viewerRef
    );
    const buyer = useFragment(
        graphql`
            fragment CheckoutPayPalWrapper_buyer on User {
                serviceId
                ...useCheckoutIsLoggedIntoPayPal_buyer
                ...usePayPalPaymentMethod_buyer
            }
        `,
        buyerRef
    );

    const relayEnvironment = useRelayEnvironment();
    const { isMobile } = useContext(CheckoutServerVarsContext);
    const { email } = usePayPalPaymentMethod({ buyer });
    const isLoggedIntoPayPal = useCheckoutIsLoggedIntoPayPal({
        buyer,
        shippingAddressId,
    });

    const paypalClient = usePayPalClient({ viewer });

    function onLogin(event) {
        event.preventDefault();
        trackUserInteractionOnce();
        const guestUserId = getGuestUserId();

        // Because tokenization opens a popup, this has to be called as a result of an action
        paypalClient.tokenize(
            {
                flow: 'vault',
            },
            async (tokenizeError, payload) => {
                if (tokenizeError) {
                    if (tokenizeError.type !== 'CUSTOMER') {
                        logCheckoutError(tokenizeError, 'paypal');
                    }
                    return;
                }

                onTogglePayPalSaving(true);

                onResetPayPalError();

                try {
                    const captchaResults = await executeCaptcha({
                        action: 'checkoutVaultPayPal',
                        featureFlag: 'vaultCreditCardCaptcha',
                        featureFlagV3: 'vaultCreditCardCaptchaV3',
                        relayEnvironment,
                    });

                    // Tokenization succeeded
                    const response = await CheckoutMutationWrapper.commit(
                        'UserVaultPaymentMethodMutation',
                        relayEnvironment,
                        {
                            userId: buyer.serviceId,
                            saveToWallet: !guestUserId,
                            mockWallet: !!guestUserId,
                            paymentMethodNonce: payload.nonce,
                            verifyPaymentMethod: shouldVerifyPaymentMethod(),
                            captchaToken: captchaResults?.captchaToken,
                            captchaKey: captchaResults?.captchaKey,
                        },
                        isMobile,
                        guestUserId
                            ? store =>
                                  guestCheckoutWalletUpdater(
                                      store,
                                      viewer.id,
                                      'userVaultPaymentMethod'
                                  )
                            : null
                    );
                    const savedToken =
                        response?.userVaultPaymentMethod?.walletPaymentMethod?.paymentMethod?.token;
                    trackCompletedStep(STEPS.STEP_PAYMENT);
                    onPayPalVaulted(savedToken);
                    onTogglePayPalSaving(false);
                } catch (e) {
                    const error = get(e, 'source.errors[0]');
                    onPayPalError(error);
                    onTogglePayPalSaving(false);
                }
            }
        );
    }
    function onRemove(event) {
        event.preventDefault();

        onTogglePayPalRemoving(true);

        CheckoutMutationWrapper.commit(
            'UserDeletePaymentMethodMutation',
            relayEnvironment,
            {
                userId: buyer.serviceId,
                paymentMethodType: 'PAYPAL_ACCOUNT',
                paymentMethodKey: email || '',
            },
            isMobile
        )
            .then(() => {
                onPayPalRemoved(lastValidCreditCardToken);
                onTogglePayPalRemoving(false);
            })
            .catch(() => {
                onTogglePayPalRemoving(false);
            });
    }

    if (isPaymentLoading) {
        return (
            <div className={wrapperClass}>
                <LocalSpinner wrapperClass={styles.spinnerWrapper} />
            </div>
        );
    }

    return (
        <div className={classNames(wrapperClass, styles.wrapper)} data-tn="checkout-paypal-wrapper">
            <RowContainer
                noMargin
                isClickable={false}
                noHorizontalPadding={isMobile}
                wrapperClass={styles.rowContainerWrapper}
            >
                <p className={styles.information} data-tn="billing-form-help-text">
                    {isLoggedIntoPayPal ? (
                        <FormattedMessage
                            id="checkout.CheckoutPayPalWrapper.selectedHelp"
                            defaultMessage="You have selected PayPal as your payment method."
                        />
                    ) : (
                        <FormattedMessage
                            id="checkout.newCheckoutPayPalWrapper.notSelectedHelp"
                            defaultMessage="Click the button below to authorize payment through your PayPal account. You will have a chance to review all of your details before submitting your order."
                        />
                    )}
                </p>
                {isLoggedIntoPayPal && (
                    <div>
                        <strong className="fs-block">{email}</strong>
                        {!isPayPalRemoving && (
                            <Link className={styles.removeLink} onClick={onRemove}>
                                <span
                                    className={styles.removeIcon}
                                    data-tn="paypal-checkout-remove-icon"
                                >
                                    ✕
                                </span>
                                <CheckoutRemoveAction />
                            </Link>
                        )}
                    </div>
                )}
                {!isLoggedIntoPayPal && !isPayPalSaving && paypalClient && (
                    <Link className={styles.paypalLink} onClick={onLogin}>
                        <img
                            src="https://a.1stdibscdn.com/dist/adhoc/app-buyer-commerce/pay-with-paypal.png"
                            className={styles.paypalIcon}
                            alt="PayPal"
                            data-tn="login-paypal-button"
                        />
                    </Link>
                )}
            </RowContainer>
        </div>
    );
};

CheckoutPayPalWrapper.propTypes = {
    relay: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired,
    buyer: PropTypes.object.isRequired,
    wrapperClass: PropTypes.string,
    shippingAddressId: PropTypes.string.isRequired,
    payPalError: PropTypes.string,
    lastValidCreditCardToken: PropTypes.string,
    onPayPalRemoved: PropTypes.func,
    onPayPalVaulted: PropTypes.func.isRequired,
    onResetPayPalError: PropTypes.func.isRequired,
    onPayPalError: PropTypes.func.isRequired,
    isPayPalSaving: PropTypes.bool.isRequired,
    isPayPalRemoving: PropTypes.bool.isRequired,
    onTogglePayPalRemoving: PropTypes.func.isRequired,
    onTogglePayPalSaving: PropTypes.func.isRequired,
    isPaymentLoading: PropTypes.bool,
};

const ConnectedCheckoutPayPalWrapper = connect(
    ({ transaction, payment }) => ({
        shippingAddressId: transaction.shippingAddressId,
        lastValidCreditCardToken: payment.lastValidCreditCardToken,
        isPayPalSaving: payment.isPayPalSaving,
        isPayPalRemoving: payment.isPayPalRemoving,
    }),
    {
        onPayPalRemoved: payPalRemoved,
        onPayPalVaulted: payPalVaulted,
        onResetPayPalError: resetPayPalError,
        onPayPalError: payPalErrorAction,
        onTogglePayPalSaving: togglePayPalSaving,
        onTogglePayPalRemoving: togglePayPalRemoving,
    }
)(CheckoutPayPalWrapper);

export default ConnectedCheckoutPayPalWrapper;
